import { useEffect, useId, useState } from "react";
import {
  MAX_ZNAKOW_KOMENTARZA,
  MAX_ZNAKOW_WARIANTU,
  type RecenzjaWariant,
  type RecenzjaWskazanie,
  type Werdykt,
} from "@shared/recenzjeTypy.js";
import { OPOZNIENIE_TEKSTU_MS } from "../../hooks/useAutozapis.js";
import { useAkcjePrzegladu } from "../../hooks/usePrzegladRecenzji.js";
import { odmien } from "../../lib/polski.js";
import {
  czyPokazacPolaTekstowe,
  opisGrupyWskazania,
  podpowiedzWerdyktu,
  szkicZKorekty,
  type KorektaSzkic,
} from "../../lib/recenzja.js";
import { Etykieta, tonRodzaju } from "../ocena/Wskazania.js";
import { Badge } from "../ui/Badge.js";
import { Textarea } from "../ui/Field.js";
import { PrzelacznikWerdyktu } from "./PrzelacznikWerdyktu.js";

/**
 * Jedno wskazanie modelu z blokiem przegladu pod spodem: dowod (cytat), uzasadnienie, lepsze warianty AI, a potem
 * decyzja recenzenta.
 *
 * Kolory oceny wg decyzji autora metodyki (29.10.2026): mocna strona i lepszy wariant sa zlote, rzecz do poprawy szara.
 * Rodzaj zawsze niesie tez slowo w plakietce (ST-3). Przelacznik werdyktu zostaje neutralny: to zdanie recenzenta
 * o wskazaniu, a nie ocena rozmowy.
 *
 * Domyslnie zakladamy zgode (nic nie kliknieto = nic nie zapisano), a "Zgadzam sie" jest swiadomym werdyktem. Komentarz
 * i wlasna wersja wypowiedzi pojawiaja sie przy "Czesciowo" i "Nie zgadzam sie" (wlasna wersja tylko przy wskazaniach do
 * poprawy), a takze zawsze, gdy cos w nich juz stoi, zeby zapisany tekst nie znikal z widoku po zmianie werdyktu.
 *
 * Szkic pol zyje w STANIE TEGO KOMPONENTU, nie w danych z serwera: kolejna odpowiedz serwera ani odswiezenie listy go nie
 * podmieni. Kolejka zapisu (usePrzegladRecenzji) trzyma tylko najnowsza wartosc do wyslania.
 */

function Warianty({
  warianty,
  wariantRecenzenta,
}: {
  warianty: readonly RecenzjaWariant[];
  wariantRecenzenta: string;
}): React.JSX.Element {
  const jestWariantRecenzenta = wariantRecenzenta.trim().length > 0;
  return (
    <div className="wskazanie__warianty">
      {/* Lepszy wariant to rozwiazanie problemu, wiec ma ton mocnej strony (zloto), jak w karcie oceny. */}
      {warianty.length > 0 ? (
        <Etykieta ton="zloto">
          {odmien(warianty.length, "Lepszy wariant", "Lepsze warianty", "Lepsze warianty")}
        </Etykieta>
      ) : (
        <p className="wskazanie__uwaga">Model nie podał lepszego wariantu tej wypowiedzi.</p>
      )}
      {(warianty.length > 0 || jestWariantRecenzenta) && (
        <ol className="warianty" role="list">
          {warianty.map((w, i) => (
            <li className="wariant" key={i}>
              <p className="wariant__tekst">{w.tekst}</p>
              {w.dlaczegoLepiej.trim().length > 0 && (
                <p className="wariant__powod">
                  <Etykieta>Dlaczego lepiej: </Etykieta>
                  {w.dlaczegoLepiej}
                </p>
              )}
            </li>
          ))}
          {jestWariantRecenzenta && (
            <li className="wariant wariant--recenzent">
              <p className="wariant__tekst">{wariantRecenzenta}</p>
              <p className="wariant__powod">
                <Etykieta>Wersja recenzenta</Etykieta>
              </p>
            </li>
          )}
        </ol>
      )}
    </div>
  );
}

export function WskazanieRecenzji({ wskazanie }: { wskazanie: RecenzjaWskazanie }): React.JSX.Element {
  const akcje = useAkcjePrzegladu();
  const idBazowe = useId();
  // Szkic niezapisany w poprzedniej wizycie (zapis sie nie udal, a recenzent wyszedl z rozmowy) wygrywa z tym, co ma serwer.
  const [szkic, setSzkic] = useState<KorektaSzkic>(() => akcje.wczytanySzkic(wskazanie.klucz) ?? szkicZKorekty(wskazanie.korekta));
  /** Ostatnia wersja recenzenta, ktora serwer FAKTYCZNIE przyjal: pokazujemy ja obok propozycji AI dopiero wtedy. */
  const [zapisanyWariant, setZapisanyWariant] = useState(wskazanie.korekta?.wariant ?? "");
  // Przywrocony szkic zapisuje hook, nie ten komponent: tu tylko czekamy na wynik, zeby pokazac "Wersje recenzenta".
  useEffect(
    () => akcje.nasluchujZapisu(wskazanie.klucz, (zapisany) => setZapisanyWariant(zapisany.wariant)),
    [akcje, wskazanie.klucz],
  );
  const mocna = wskazanie.rodzaj === "mocna-strona";
  const bezCytatuWTranskrypcie = Boolean(wskazanie.cytat) && !wskazanie.cytatZweryfikowany;

  function zmien(nowy: KorektaSzkic, opoznienieMs: number): void {
    setSzkic(nowy);
    akcje.zapiszKorekte(wskazanie.klucz, nowy, opoznienieMs, (zapisany) => setZapisanyWariant(zapisany.wariant));
  }

  function zmienWerdykt(werdykt: Werdykt | null): void {
    // Licznik rusza od razu; odpowiedz serwera go potem potwierdzi albo skoryguje.
    if (szkic.werdykt === null && werdykt !== null) akcje.zmienOcenionych(1);
    else if (szkic.werdykt !== null && werdykt === null) akcje.zmienOcenionych(-1);
    zmien({ ...szkic, werdykt }, 0);
  }

  const koniecTekstu = (): void => akcje.splusczKorekte(wskazanie.klucz);
  // Nazwa grupy odroznia wskazania tego samego parametru (rodzaj, poczatek cytatu); unikalnosc w obrebie rozmowy daje hook.
  const opisGrupy = akcje.opisGrupy(wskazanie.klucz) ?? opisGrupyWskazania(wskazanie);

  return (
    <article className="rec-wskazanie" data-rodzaj={wskazanie.rodzaj}>
      <div className="rec-wskazanie__head">
        <Badge wariant={tonRodzaju(wskazanie.rodzaj)} kropka>
          {mocna ? "Mocna strona" : "Do poprawy"}
        </Badge>
        <Badge>{wskazanie.parametr}</Badge>
        <Badge wariant="info">Propozycja AI</Badge>
        {bezCytatuWTranskrypcie && <Badge>cytat niezweryfikowany</Badge>}
      </div>

      {wskazanie.cytat && <blockquote className="wskazanie__cytat">{wskazanie.cytat}</blockquote>}
      {bezCytatuWTranskrypcie && (
        <p className="wskazanie__uwaga">
          Nie znaleźliśmy tego cytatu w wypowiedzi słowo w słowo, więc nie zaznaczamy go w transkrypcie.
        </p>
      )}

      {wskazanie.uzasadnienie && (
        <p className="wskazanie__opis">
          <Etykieta ton={tonRodzaju(wskazanie.rodzaj)}>{mocna ? "Dlaczego to dobre: " : "Jak to obniża wpływ: "}</Etykieta>
          {wskazanie.uzasadnienie}
        </p>
      )}
      {mocna && wskazanie.jakRozwijac && (
        <p className="wskazanie__opis">
          <Etykieta>Jak rozwijać: </Etykieta>
          {wskazanie.jakRozwijac}
        </p>
      )}
      {!mocna && <Warianty warianty={wskazanie.warianty} wariantRecenzenta={zapisanyWariant} />}

      <div className="rec-przeglad">
        <p className="rec-przeglad__pytanie">Czy zgadzasz się z tym wskazaniem?</p>
        <PrzelacznikWerdyktu etykieta={opisGrupy} wartosc={szkic.werdykt} onZmiana={zmienWerdykt} />
        <span className="field-hint">{podpowiedzWerdyktu(szkic.werdykt, wskazanie.rodzaj)}</span>

        {czyPokazacPolaTekstowe(szkic) && (
          <div className="rec-przeglad__wiecej">
            <Textarea
              etykieta="Twój komentarz"
              id={`${idBazowe}-komentarz`}
              placeholder="Dlaczego? Co model przeoczył?"
              maxLength={MAX_ZNAKOW_KOMENTARZA}
              value={szkic.komentarz}
              onChange={(e) => zmien({ ...szkic, komentarz: e.target.value }, OPOZNIENIE_TEKSTU_MS)}
              onBlur={koniecTekstu}
            />
            {!mocna && (
              <Textarea
                etykieta="Twoja wersja wypowiedzi"
                id={`${idBazowe}-wariant`}
                placeholder="Jak powiedziałbyś to Ty?"
                hint="Pojawi się obok propozycji AI jako „Wersja recenzenta”."
                maxLength={MAX_ZNAKOW_WARIANTU}
                value={szkic.wariant}
                onChange={(e) => zmien({ ...szkic, wariant: e.target.value }, OPOZNIENIE_TEKSTU_MS)}
                onBlur={koniecTekstu}
              />
            )}
          </div>
        )}
      </div>
    </article>
  );
}
